/* eslint-disable no-nested-ternary */
import { useEffect } from "react";
import { ChatWidgetContent } from "./ChatWidgetContent";
import { ChatWidgetCurrent } from "../../model/chat-wiget/types/ChatWigetCurrent.enum";
import { Button } from "../Button";
import styled, { keyframes, css } from "styled-components";
import { Shadow } from "../../theme/Shadow";
import { Rounded } from "../../theme/Rounded";
import { Flex } from "../Flex";
import { Spacing } from "../../theme/Spacing";
import { appSliceSelectors } from "../../redux/app/app.selector";
import { useAppSelector } from "../../redux/store";
import { useTranslation } from "react-i18next";
import { AtiraImage } from "../Image";
import { AtiraIcon } from "../AtiraIcon";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { faUpDownLeftRight } from "@fortawesome/free-solid-svg-icons/faUpDownLeftRight";
import { useTheme } from "../../ThemeContext";
import { getChatImage } from "../../utils/Media";
import { useDrawersContext } from "../../routes/tasks/drawers-context";
import { TestIDs } from "../../tests/types/testIds";
import {
  useDraggableFahmyButton,
  FAHMY_BUTTON_SIZE_PX,
} from "./useDraggableFahmyButton";

const swayIdle = keyframes`
  0%, 100% { transform: rotate(0deg) translateY(0); }
  25% { transform: rotate(2deg) translateY(-2px); }
  50% { transform: rotate(-1deg) translateY(1px); }
  75% { transform: rotate(1deg) translateY(0); }
`;

const excitedBounce = keyframes`
  0%, 100% { transform: rotate(0deg) translateY(0); }
  20% { transform: rotate(5deg) translateY(-4px); }
  40% { transform: rotate(-3deg) translateY(2px); }
  60% { transform: rotate(3deg) translateY(-2px); }
  80% { transform: rotate(-2deg) translateY(1px); }
`;

const floatingChat = keyframes`
  0%, 100% { transform: translateY(0); }
  25% { transform: translateY(-3px); }
  50% { transform: translateY(2px); }
  75% { transform: translateY(-1px); }
`;

const hoverWiggle = keyframes`
  0%, 100% { transform: rotate(0deg); }
  20% { transform: rotate(8deg); }
  40% { transform: rotate(-6deg); }
  60% { transform: rotate(6deg); }
  80% { transform: rotate(-4deg); }
`;

const ButtonWrapper = styled(Flex)<{
  $left?: number;
  $top?: number;
  $draggable?: boolean;
  $dragging?: boolean;
}>`
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.white};
  border-radius: ${Rounded.circle};
  position: fixed;
  ${({ $left, $top }) =>
    $left !== undefined && $top !== undefined
      ? css`
          left: ${$left}px;
          top: ${$top}px;
        `
      : css`
          bottom: ${Spacing.xxl};
          inset-inline-end: ${Spacing.l};
        `}
  cursor: ${({ $draggable, $dragging }) =>
    $dragging ? "grabbing" : $draggable ? "grab" : "pointer"};
  touch-action: none;
  z-index: 1002;
  border: ${({ theme }) => `3px solid ${theme.main}`};
  box-shadow: ${Shadow.MD};
  transition:
    transform 0.3s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }
`;

type TooltipPlacement = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

const toPx = (value?: number) => (value === undefined ? "auto" : `${value}px`);

const TooltipBubble = styled.p<{
  visible: boolean;
  $placement?: TooltipPlacement;
}>`
  position: fixed;
  ${({ $placement }) =>
    $placement
      ? css`
          top: ${toPx($placement.top)};
          right: ${toPx($placement.right)};
          bottom: ${toPx($placement.bottom)};
          left: ${toPx($placement.left)};
        `
      : css`
          bottom: calc(${Spacing.xxl} + 3.5rem + ${Spacing.m});
          inset-inline-end: 1.5rem;
        `}
  background-color: #ffffff;
  padding: ${Spacing.s} ${Spacing.m};
  border-radius: ${Rounded.xl};
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  font-size: 0.875rem;
  max-width: 18rem;
  color: #1f2937;
  cursor: pointer;

  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transform: ${({ visible }) =>
    visible ? "translateY(0)" : "translateY(10px)"};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  pointer-events: ${({ visible }) => (visible ? "auto" : "none")};
  z-index: 9999999999999;
`;

const TOOLTIP_GAP_PX = 16;
const TOOLTIP_WIDTH_PX = 320;
const TOOLTIP_HEIGHT_PX = 140;

const getTooltipPlacement = (
  button: { left: number; top: number },
  vw: number,
  vh: number,
): TooltipPlacement => ({
  ...(vw - button.left < TOOLTIP_WIDTH_PX
    ? { right: vw - button.left - FAHMY_BUTTON_SIZE_PX }
    : { left: button.left }),
  ...(button.top < TOOLTIP_HEIGHT_PX
    ? { top: button.top + FAHMY_BUTTON_SIZE_PX + TOOLTIP_GAP_PX }
    : { bottom: vh - button.top + TOOLTIP_GAP_PX }),
});

const DragHint = styled.span`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  margin-top: ${Spacing.xs};
  font-size: 0.75rem;
  opacity: 0.7;
`;

const CloseIconWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme }) => theme.white};
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const StyledButton = styled(Button)<{
  menuOpen: boolean;
  current: ChatWidgetCurrent;
  isHovered: boolean;
}>`
  margin: 0;
  padding: 0;
  height: 3.5rem;
  width: 3.5rem;
  border-radius: ${Rounded.circle};
  box-shadow: ${Shadow.MD};
  z-index: 1002;
  position: relative;
  overflow: hidden;
  cursor: inherit;
  background-color: ${({ theme }) => theme.white};

  .ant-image {
    position: absolute;
    inset: 0;
    transition: transform 0.4s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ menuOpen, current, isHovered }) => css`
      animation: ${isHovered
        ? css`
            ${hoverWiggle} 0.5s infinite ease-in-out
          `
        : menuOpen
          ? current === ChatWidgetCurrent.CHAT
            ? css`
                ${floatingChat} 2s infinite ease-in-out
              `
            : css`
                ${excitedBounce} 2s infinite ease-in-out
              `
          : css`
              ${swayIdle} 2s infinite ease-in-out
            `};
    `}
  }

  &:hover ${CloseIconWrapper} {
    opacity: 1;
  }
`;

export const ChatWidget: React.FC = () => {
  const {
    fahmyCurrent,
    setFahmyCurrent,
    fahmyIsHovered,
    setFahmyIsHovered,
    fahmyMenuOpen,
    setFahmyMenuOpen,
    fahmyShowTooltip,
    setFahmyShowTooltip,
  } = useDrawersContext();

  const { selectedTheme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "floatingMenu.icon" });

  const isWidgetVisible =
    useAppSelector(appSliceSelectors.selectChatWidgetVisibility) === true;

  const {
    elementRef: buttonDragRef,
    onPointerDown: onButtonPointerDown,
    onDragStart: onButtonDragStart,
    position: buttonPosition,
    isDragging,
    justDraggedRef,
  } = useDraggableFahmyButton(!fahmyMenuOpen);

  const tooltipPlacement = buttonPosition
    ? getTooltipPlacement(buttonPosition, window.innerWidth, window.innerHeight)
    : undefined;

  const onToggleMenu = () => {
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      return;
    }
    setFahmyMenuOpen((v) => !v);
    if (!window.localStorage.getItem("isOpenedWidgetForFirstTime")) {
      window.localStorage.setItem("isOpenedWidgetForFirstTime", "true");
    }
    setFahmyShowTooltip(false);
    setFahmyCurrent(ChatWidgetCurrent.DEFAULT);
  };

  const onOpenChat = () => {
    setFahmyMenuOpen(true);
    setFahmyCurrent(ChatWidgetCurrent.CHAT);
    setFahmyShowTooltip(false);
    window.localStorage.setItem("isOpenedWidgetForFirstTime", "true");
  };

  const onHoverOnWidget = () => {
    if (!fahmyMenuOpen) {
      setFahmyShowTooltip(true);
      setFahmyIsHovered(true);
    }
  };

  const onWidgetMouseLeave = () => {
    setFahmyIsHovered(false);
    if (window.localStorage.getItem("isOpenedWidgetForFirstTime")) {
      setFahmyShowTooltip(false);
    }
  };

  useEffect(() => {
    if (!window.localStorage.getItem("isOpenedWidgetForFirstTime")) {
      setFahmyShowTooltip(true);
    }
  }, [setFahmyShowTooltip]);

  if (!isWidgetVisible) {
    return null;
  }

  return (
    <Flex onMouseLeave={onWidgetMouseLeave}>
      <TooltipBubble
        visible={fahmyShowTooltip}
        onClick={onOpenChat}
        className={TestIDs.fahmy.tooltip}
        $placement={tooltipPlacement}
      >
        {t("tooltip")}

        <DragHint>
          <AtiraIcon icon={faUpDownLeftRight} size="xs" />
          {t("dragHint")}
        </DragHint>
      </TooltipBubble>

      <ButtonWrapper
        ref={buttonDragRef}
        onPointerDown={onButtonPointerDown}
        onDragStart={onButtonDragStart}
        onMouseEnter={onHoverOnWidget}
        onMouseLeave={() => setFahmyIsHovered(false)}
        dataTestId={TestIDs.fahmy.stickyWrapper}
        $left={buttonPosition?.left}
        $top={buttonPosition?.top}
        $draggable={!fahmyMenuOpen}
        $dragging={isDragging}
      >
        <StyledButton
          menuOpen={fahmyMenuOpen}
          current={fahmyCurrent}
          isHovered={fahmyIsHovered}
          onClick={onToggleMenu}
        >
          <AtiraImage
            src={getChatImage(selectedTheme, fahmyMenuOpen, fahmyCurrent).image}
            className={
              getChatImage(selectedTheme, fahmyMenuOpen, fahmyCurrent).className
            }
          />
          {fahmyMenuOpen ? (
            <CloseIconWrapper>
              <AtiraIcon icon={faXmark} size="xl" color="main" />
            </CloseIconWrapper>
          ) : null}
        </StyledButton>
      </ButtonWrapper>

      <ChatWidgetContent
        open={fahmyMenuOpen}
        setOpen={setFahmyMenuOpen}
        current={fahmyCurrent}
        setCurrent={setFahmyCurrent}
        buttonAnchor={buttonPosition}
      />
    </Flex>
  );
};
